export type Metric = {
  name: string
  value: number
  unit: "kg" | "percent" | "number" | "dollars"
}

export type InstallationWithMetrics = {
  name: string,
  id: string,
  location: {
    id: string,
    name: string
  },
  metrics: Metric[]
}

export const getMatchingMetric = (installationWithMetrics: InstallationWithMetrics, metricName: string): Metric | undefined => {
  return installationWithMetrics.metrics.find((metric) => metric.name === metricName);
}

export const METRICS = [
  {
    id: "co2e_from_co2",
    name: "CO2e from CO2"
  },
  {
    id: "co2e_from_ch4",
    name: "CO2e from CH4"
  },
  {
    id: "co2e_total",
    name: "Total CO2e"
  },
  {
    id: "max_ch4_concentration",
    name: "Maximum CH4 Concentration"
  },
  {
    id: "on_durations_count",
    name: "Number of 'on' times"
  },
  {
    id: "on_durations_sum",
    name: "Total time 'on'"
  },
  {
    id: "on_durations_median",
    name: "Median time 'on'"
  },
  {
    id: "o2_when_on_median",
    name: "O2% when on (median)",
    hiddenByDefault: true,
  },
  {
    id: "o2_when_on_steady",
    name: "O2% when on (steady)"
  },
  {
    id: "heating_durations_sum",
    name: "Total heating time",
    hiddenByDefault: true,
  },
  {
    id: "co2e_per_heating_hour",
    name: "CO2e per heating hour",
    hiddenByDefault: true,
  },
  {
    id: "co2e_per_on_hour",
    name: "CO2e per on hour"
  },
  {
    id: "duty_cycle_median",
    name: "Median duty cycle",
    hiddenByDefault: true,
  },
  {
    id: "cycles_per_hour_mean",
    name: "Average cycles per hour"
  },
  // short cycling
  {
    id: "fuel_reduction_potential_short_cycling",
    name: "Fuel reduction potential (short cycling)"
  },
  {
    id: "fuel_reduction_potential_short_cycling_on_last_year_spend",
    name: "Annual fuel reduction potential (short cycling)"
  },
  {
    id: "emissions_reduction_potential_short_cycling",
    name: "Emissions reduction potential (short cycling)"
  },
  {
    id: "emissions_reduction_potential_short_cycling_on_last_year_emissions_estimate",
    name: "Annual emissions reduction potential (short cycling)"
  },
  // running lean
  {
    id: "fuel_reduction_potential_running_lean",
    name: "Fuel reduction potential (running lean)"
  },
  {
    id: "fuel_reduction_potential_running_lean_on_last_year_spend",
    name: "Annual fuel reduction potential (running lean)"
  },
  {
    id: "emissions_reduction_potential_running_lean",
    name: "Emissions reduction potential (running lean)"
  },
  {
    id: "emissions_reduction_potential_running_lean_on_last_year_emissions_estimate",
    name: "Annual emissions reduction potential (running lean)"
  },
  // fugitive methane
  {
    id: "fuel_reduction_potential_fugitive_methane",
    name: "Fuel reduction potential (fugitive methane)"
  },
  {
    id: "fuel_reduction_potential_fugitive_methane_on_last_year_spend",
    name: "Annual fuel reduction potential (fugitive methane)"
  },
  {
    id: "emissions_reduction_potential_fugitive_methane",
    name: "Emissions reduction potential (fugitive methane)"
  },
  {
    id: "emissions_reduction_potential_fugitive_methane_on_last_year_emissions_estimate",
    name: "Annual emissions reduction potential (fugitive methane)"
  },
  // total
  {
    id: "fuel_reduction_potential_total",
    name: "Fuel reduction potential (total)"
  },
  {
    id: "emissions_reduction_potential_total",
    name: "Emissions reduction potential (total)"
  },
  {
    id: "fuel_reduction_potential_on_last_year_spend",
    name: "Annual fuel reduction potential (total)"
  },
  {
    id: "emissions_reduction_potential_on_last_year_emissions_estimate",
    name: "Annual emissions reduction potential (total)"
  },
  // refrigerant
  {
    id: "ratio_of_compressor_temp_to_critical_temp",
    name: "Ratio of compressor temperature to critical temperature"
  },
  {
    id: "ratio_of_compressor_temp_to_critical_temp_when_on",
    name: "Ratio of compressor temperature to critical temperature when on"
  },
  {
    id: "max_sniff_concentration",
    name: "Maximum refrigerant concentration"
  },
  {
    id: "sniff_concentration_count",
    name: "Number of refrigerant detections"
  },
]

export const getInitialColumnVisibility = (): Record<string, boolean> => {
  return Object.fromEntries(
    METRICS
      .filter(({ hiddenByDefault }) => hiddenByDefault)
      .map(({ id }) => ([id, false]))
  )
};
